import React, { useMemo, useState } from "react";
import { Box, Typography, InputBase } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import MenuBookRoundedIcon from "@material-ui/icons/MenuBookRounded";
import MEDICINE_MATRIX from "./medicineMatrixData";

// Drill-down view of the classical medicine matrix: pick a category on the
// left, its seven medicine forms render as cards on the right. This keeps a
// 33-row x 8-column matrix legible inside the recommendation panel without
// horizontal scrolling. Data is a static prototype (see medicineMatrixData.js).

const useStyles = makeStyles((theme) => ({
  shell: {
    display: "grid",
    gridTemplateColumns: "260px 1fr",
    border: "1px solid #eef0f3",
    borderRadius: 12,
    overflow: "hidden",
    minHeight: 460,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  rail: {
    borderRight: "1px solid #eef0f3",
    backgroundColor: "#fafbfc",
    // Only the category list scrolls; the outer card itself never does.
    position: "sticky",
    top: 0,
    alignSelf: "start",
    maxHeight: 650,
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      position: "static",
      maxHeight: 220,
      borderRight: "none",
      borderBottom: "1px solid #eef0f3",
    },
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(1.25),
    padding: theme.spacing(0.5, 1.25),
    backgroundColor: "#fff",
    border: "1px solid #eef0f3",
    borderRadius: 8,
  },
  searchIcon: { color: "#9aa4b2", fontSize: "1.15rem" },
  searchInput: { fontSize: "0.85rem", flex: 1 },
  railSection: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#9aa4b2",
    padding: theme.spacing(1.25, 1.75, 0.5),
  },
  railItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
    padding: theme.spacing(1, 1.75),
    borderLeft: "3px solid transparent",
    color: "#374151",
    fontSize: "0.85rem",
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "rgba(17, 98, 254, 0.06)" },
  },
  railItemActive: {
    backgroundColor: "#fff",
    borderLeftColor: "#1162FE",
    fontWeight: 700,
    color: "#1162FE",
  },
  railCount: {
    fontSize: "0.72rem",
    color: "#9aa4b2",
    fontVariantNumeric: "tabular-nums",
  },
  detail: {
    padding: theme.spacing(2.5),
  },
  detailTitle: { fontSize: "1.25rem", fontWeight: 700, color: "#1f2937" },
  detailNote: {
    fontSize: "0.85rem",
    color: "#6b7280",
    marginBottom: theme.spacing(2),
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
  },
  formCard: {
    border: "1px solid #eef0f3",
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    padding: theme.spacing(1.5),
  },
  formHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  formLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "#1162FE",
  },
  formSub: { fontSize: "0.68rem", color: "#9aa4b2" },
  medList: { margin: 0, paddingLeft: theme.spacing(2) },
  medItem: {
    fontSize: "0.83rem",
    color: "#374151",
    lineHeight: 1.7,
    "&::marker": { color: "#9ab4ff" },
  },
  sourceBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: "1px dashed #d6dae0",
    backgroundColor: "#fafbfc",
  },
  sourceHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: theme.spacing(0.75),
  },
  sourceIcon: { fontSize: "0.95rem", color: "#9aa4b2" },
  sourceLine: { fontSize: "0.78rem", color: "#6b7280", lineHeight: 1.6 },
  empty: { padding: theme.spacing(4), textAlign: "center", color: "#9aa4b2" },
}));

const MedicineMatrix = () => {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState({ s: 0, r: 0 });

  // Flatten to rows tagged with their section, then filter by the search term.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MEDICINE_MATRIX.sections
      .map((sec) => ({
        section: sec.section,
        rows: sec.rows
          .map((row, r) => ({ ...row, _r: r }))
          .filter((row) => {
            if (!q) return true;
            if (row.name.toLowerCase().includes(q)) return true;
            return row.cells.some((cell) =>
              cell.some((m) => m.toLowerCase().includes(q))
            );
          }),
      }))
      .filter((sec) => sec.rows.length > 0);
  }, [query]);

  // Resolve the active row against the (possibly filtered) list.
  const active = useMemo(() => {
    const sec = MEDICINE_MATRIX.sections[selected.s];
    const row = sec && sec.rows[selected.r];
    if (row) return { section: sec.section, row };
    const first = groups[0];
    return first ? { section: first.section, row: first.rows[0] } : null;
  }, [selected, groups]);

  return (
    <Box className={classes.shell}>
      <Box className={classes.rail}>
        <Box className={classes.searchBox}>
          <SearchRoundedIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Search category or medicine…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        {groups.map((sec) => {
          const si = MEDICINE_MATRIX.sections.findIndex(
            (x) => x.section === sec.section
          );
          return (
            <Box key={sec.section}>
              <Typography className={classes.railSection}>
                {sec.section}
              </Typography>
              {sec.rows.map((row) => {
                const isActive =
                  active &&
                  active.section === sec.section &&
                  active.row.name === row.name;
                const count = row.cells.reduce((n, c) => n + c.length, 0);
                return (
                  <button
                    type="button"
                    key={row.name}
                    className={`${classes.railItem} ${
                      isActive ? classes.railItemActive : ""
                    }`}
                    onClick={() => setSelected({ s: si, r: row._r })}
                  >
                    <span>{row.name}</span>
                    <span className={classes.railCount}>{count}</span>
                  </button>
                );
              })}
            </Box>
          );
        })}
      </Box>

      <Box className={classes.detail}>
        {!active ? (
          <Box className={classes.empty}>
            <Typography>No categories match “{query}”.</Typography>
          </Box>
        ) : (
          <>
            <Typography className={classes.detailTitle}>
              {active.row.name}
            </Typography>
            {active.row.note ? (
              <Typography className={classes.detailNote}>
                {active.row.note}
              </Typography>
            ) : (
              <Box mb={2} />
            )}

            <Box className={classes.cardGrid}>
              {active.row.cells.map((cell, i) =>
                cell.length ? (
                  <Box key={i} className={classes.formCard}>
                    <Box className={classes.formHead}>
                      <span className={classes.formLabel}>
                        {MEDICINE_MATRIX.forms[i].label}
                      </span>
                      <span className={classes.formSub}>
                        {MEDICINE_MATRIX.forms[i].sub}
                      </span>
                    </Box>
                    <ul className={classes.medList}>
                      {cell.map((m, j) => (
                        <li key={j} className={classes.medItem}>
                          {m}
                        </li>
                      ))}
                    </ul>
                  </Box>
                ) : null
              )}
            </Box>

            {active.row.source && active.row.source.length > 0 && (
              <Box className={classes.sourceBox}>
                <Box className={classes.sourceHead}>
                  <MenuBookRoundedIcon className={classes.sourceIcon} />
                  {MEDICINE_MATRIX.sourceLabel}
                </Box>
                {active.row.source.map((line, i) => (
                  <Typography key={i} className={classes.sourceLine}>
                    {line}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MedicineMatrix;
